import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Upload, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getDispatchDocSignedUrl } from "@/hooks/useDispatchDocuments";

const DOC_TYPE_LABEL: Record<string, string> = {
  bol: "BOL", pod: "POD", rate_con: "Rate Con", photo: "Photo", other: "Other",
};
const DOC_TYPE_TONE: Record<string, string> = {
  bol: "bg-sky-500/10 text-sky-700 border-sky-500/30",
  pod: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  rate_con: "bg-brand-teal/15 text-brand-teal border-brand-teal/30",
  photo: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  other: "bg-muted text-muted-foreground border-border",
};

function fmtSize(b: number | null) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  loadId: string;
  documents: any[];
  dispatchExists: boolean;
}

export function DocumentsTab({ loadId, documents, dispatchExists }: Props) {
  const navigate = useNavigate();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (d: { id: string; file_path: string; file_name: string }) => {
    setDownloadingId(d.id);
    try {
      const url = await getDispatchDocSignedUrl(d.file_path);
      if (!url) throw new Error("Could not generate link");
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener"; a.download = d.file_name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e: any) {
      toast.error(e?.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  if (!dispatchExists || documents.length === 0) {
    return (
      <Card className="p-6 text-center">
        <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-medium mb-1">No documents yet</p>
        <p className="text-xs text-muted-foreground mb-3">
          Upload BOL, POD, rate confirmations, and photos from the dispatch builder.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 text-xs"
          onClick={() => navigate(`/lr-direct/dispatches/build/${loadId}`)}
        >
          <Upload className="h-3.5 w-3.5" /> Open Builder to Upload
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
          Documents ({documents.length})
        </h3>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 text-xs"
          onClick={() => navigate(`/lr-direct/dispatches/build/${loadId}`)}
        >
          <Upload className="h-3 w-3" /> Upload in Builder
        </Button>
      </div>
      <div className="divide-y divide-border">
        {documents.map((d) => (
          <div key={d.id} className="py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{d.file_name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {d.uploader_name ?? "User"} · {new Date(d.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  {d.file_size ? ` · ${fmtSize(d.file_size)}` : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge variant="outline" className={`text-[10px] uppercase font-bold ${DOC_TYPE_TONE[d.doc_type] ?? DOC_TYPE_TONE.other}`}>
                {DOC_TYPE_LABEL[d.doc_type] ?? d.doc_type}
              </Badge>
              <Button
                size="sm" variant="ghost" className="h-7 w-7 p-0"
                onClick={() => handleDownload(d)}
                disabled={downloadingId === d.id}
              >
                {downloadingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
